import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TbArchive, TbArrowBackUp, TbCamera, TbChevronLeft, TbChevronRight, TbCircleCheck, TbPencil, TbPhoto, TbPhotoEdit,
  TbPhotoPlus, TbPlus, TbRefresh, TbSearch, TbStar, TbTag, TbTrash, TbUser, TbX,
} from 'react-icons/tb';
import { ConfirmationSheet } from '../components/ConfirmationSheet';
import { ExerciseGif } from '../training/ExerciseGif';
import { resizeImageFile } from '../lib/resize-image';
import { exerciseAdminApi } from './exercise-admin-api';
import { ExerciseFormSheet, ExerciseRenameSheet } from './ExerciseForms';
import './exercises.css';

const PAGE_SIZE = 24;
const STEPS = ['Posición inicial', 'Posición final', 'Vista previa'];

function readDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('No se pudo procesar la imagen.'));
    reader.readAsDataURL(blob);
  });
}

export function ExercisesPage({
  api = exerciseAdminApi,
  mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined,
  resizeImage = resizeImageFile,
}) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [onlyCustom, setOnlyCustom] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [formTarget, setFormTarget] = useState(null); // { exercise? }: alta o edición de un propio
  const [renameTarget, setRenameTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [createdExercise, setCreatedExercise] = useState(null);
  const [offset, setOffset] = useState(0);
  const [payload, setPayload] = useState({ status: 'loading', exercises: [], total: 0, message: '' });
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedQuery(query.trim()); setOffset(0); }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(() => {
    const requestId = ++requestRef.current;
    setPayload((old) => ({ ...old, status: old.exercises.length ? 'ready' : 'loading' }));
    // Se listan todos (con y sin imagen): cada filtro lo resuelve el servidor sobre el catálogo entero.
    api.listExercises({
      q: debouncedQuery || undefined,
      limit: PAGE_SIZE,
      offset,
      missingMedia: onlyMissing ? 1 : undefined,
      custom: onlyCustom ? 1 : undefined,
      includeArchived: showArchived ? 1 : undefined,
    })
      .then((result) => {
        if (requestRef.current !== requestId) return;
        setPayload({ status: 'ready', exercises: result.exercises || [], total: result.total || 0, message: '' });
      })
      .catch((error) => {
        if (requestRef.current !== requestId) return;
        setPayload({ status: 'error', exercises: [], total: 0, message: error.message || 'No se pudo cargar el catálogo.' });
      });
  }, [api, debouncedQuery, offset, onlyMissing, onlyCustom, showArchived]);
  useEffect(() => {
    load();
    return () => { requestRef.current += 1; };
  }, [load]);

  const visibleExercises = payload.exercises;

  function toggleFilter(setter) {
    return (event) => { setter(event.target.checked); setOffset(0); };
  }

  function handleExerciseSaved(saved, wasEditing) {
    setFormTarget(null);
    setCreatedExercise(wasEditing ? null : saved);
    setToast(wasEditing ? 'Ejercicio actualizado.' : 'Ejercicio creado.');
    load();
  }

  function handleRenamed(saved) {
    setRenameTarget(null);
    setCreatedExercise(null);
    setToast(saved && saved.hasCustomName ? 'Nombre actualizado.' : 'Nombre original restaurado.');
    load();
  }

  async function archiveExercise() {
    const exercise = archiveTarget;
    if (!exercise) return;
    setBusyId(exercise.id);
    try {
      await api.archiveExercise(exercise.id);
      setCreatedExercise(null);
      setToast('Ejercicio archivado.');
      load();
    } catch (error) {
      setToast(error.message || 'No se pudo archivar el ejercicio.');
    } finally { setBusyId(null); setArchiveTarget(null); }
  }

  async function restoreExercise(exercise) {
    setBusyId(exercise.id);
    try {
      await api.unarchiveExercise(exercise.id);
      setCreatedExercise(null);
      setToast('Ejercicio restaurado.');
      load();
    } catch (error) {
      setToast(error.message || 'No se pudo restaurar el ejercicio.');
    } finally { setBusyId(null); }
  }

  // La confirmación es una hoja propia de la app (antes window.confirm), con los mismos textos.
  async function removeMedia() {
    const exercise = pendingRemoval;
    if (!exercise) return;
    setBusyId(exercise.id);
    try {
      await api.deleteExerciseMedia(exercise.id);
      setCreatedExercise(null);
      setToast(exercise.hasOriginalMedia ? 'Imagen original restaurada' : 'Imagen quitada');
      load();
    } catch (error) {
      setToast(error.message || 'No se pudo actualizar la imagen.');
    } finally { setBusyId(null); setPendingRemoval(null); }
  }

  return (
    <section className="gym-page gym-exercises" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-exercises__header">
        <h1 id="gym-page-title" className="gym-page__title">Ejercicios</h1>
        <button type="button" className="gym-button gym-button--primary gym-exercises__new" onClick={() => setFormTarget({})}>
          <TbPlus aria-hidden="true" focusable="false" />Nuevo ejercicio
        </button>
      </header>
      <div className="gym-exercises__filters">
        <label className="gym-exercises__search">
          <span className="gym-sr-only">Buscar ejercicios</span>
          <TbSearch aria-hidden="true" focusable="false" />
          <input aria-label="Buscar ejercicios" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar ejercicios" />
        </label>
        <div className="gym-exercises__checks">
          <label className="gym-exercises__check"><input type="checkbox" checked={onlyMissing} onChange={toggleFilter(setOnlyMissing)} /> Sólo sin imagen</label>
          <label className="gym-exercises__check"><input type="checkbox" checked={onlyCustom} onChange={toggleFilter(setOnlyCustom)} /> Sólo propios</label>
          <label className="gym-exercises__check"><input type="checkbox" checked={showArchived} onChange={toggleFilter(setShowArchived)} /> Ver archivados</label>
        </div>
      </div>
      {payload.status === 'loading' && !payload.exercises.length && <p className="gym-exercises__state" role="status">Cargando ejercicios…</p>}
      {payload.status === 'error' && <div className="gym-exercises__state gym-exercises__state--error" role="alert"><p>{payload.message}</p><button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button></div>}
      {payload.status === 'ready' && (visibleExercises.length ? (
        <div className="gym-exercises__list" aria-label="Catálogo de ejercicios">
          {visibleExercises.map((exercise) => (
            <article className={`gym-exercise-row${exercise.archived ? ' gym-exercise-row--archived' : ''}`} key={exercise.id}>
              <div className="gym-exercise-row__top">
                <span className="gym-exercise-row__media"><ExerciseGif exercise={exercise} size="sm" /></span>
                <div className="gym-exercise-row__body">
                  <h2>{exercise.name}</h2>
                  {exercise.hasCustomName && exercise.originalName && <p className="gym-exercise-row__original">Original: {exercise.originalName}</p>}
                  <p>{[exercise.targetLabel || exercise.target, exercise.equipmentLabel || exercise.equipment].filter(Boolean).join(' · ') || 'Sin músculo o equipo especificado'}</p>
                  <span className="gym-exercise-row__tags">
                    {exercise.isCustom && <span className="gym-exercise-row__custom gym-exercise-row__custom--own"><TbUser aria-hidden="true" focusable="false" />Propio</span>}
                    {exercise.archived && <span className="gym-exercise-row__custom gym-exercise-row__custom--archived"><TbArchive aria-hidden="true" focusable="false" />Archivado</span>}
                    {exercise.hasCustomMedia && <span className="gym-exercise-row__custom"><TbStar aria-hidden="true" focusable="false" />Imagen propia</span>}
                  </span>
                </div>
              </div>
              {exercise.archived ? (
                <div className="gym-exercise-row__actions">
                  <button type="button" className="gym-exercise-row__action" disabled={busyId === exercise.id} onClick={() => restoreExercise(exercise)}>
                    <TbArrowBackUp aria-hidden="true" focusable="false" />Restaurar
                  </button>
                </div>
              ) : (
              <div className="gym-exercise-row__actions">
                <button type="button" className="gym-exercise-row__action" disabled={busyId === exercise.id} onClick={() => setSelected(exercise)}>
                  {exercise.hasCustomMedia ? <TbPhotoEdit aria-hidden="true" focusable="false" /> : <TbPhotoPlus aria-hidden="true" focusable="false" />}
                  {exercise.hasCustomMedia ? 'Cambiar imagen' : 'Agregar imagen'}
                </button>
                {exercise.hasCustomMedia && (
                  <button
                    type="button"
                    className={`gym-exercise-row__action${exercise.hasOriginalMedia ? '' : ' gym-exercise-row__action--danger'}`}
                    disabled={busyId === exercise.id}
                    onClick={() => setPendingRemoval(exercise)}
                  >
                    {exercise.hasOriginalMedia ? <TbArrowBackUp aria-hidden="true" focusable="false" /> : <TbTrash aria-hidden="true" focusable="false" />}
                    {exercise.hasOriginalMedia ? 'Volver a la imagen original' : 'Quitar imagen'}
                  </button>
                )}
                {exercise.isCustom ? (
                  <>
                    <button type="button" className="gym-exercise-row__action" disabled={busyId === exercise.id} onClick={() => setFormTarget({ exercise })}>
                      <TbPencil aria-hidden="true" focusable="false" />Editar
                    </button>
                    <button type="button" className="gym-exercise-row__action gym-exercise-row__action--danger" disabled={busyId === exercise.id} onClick={() => setArchiveTarget(exercise)}>
                      <TbArchive aria-hidden="true" focusable="false" />Archivar
                    </button>
                  </>
                ) : (
                  <button type="button" className="gym-exercise-row__action" disabled={busyId === exercise.id} onClick={() => setRenameTarget(exercise)}>
                    <TbTag aria-hidden="true" focusable="false" />Cambiar nombre
                  </button>
                )}
              </div>
              )}
            </article>
          ))}
        </div>
      ) : <p className="gym-exercises__empty">{onlyMissing ? 'No hay ejercicios sin imagen.' : 'No se encontraron ejercicios.'}</p>)}
      {payload.total > PAGE_SIZE && <nav className="gym-exercises__pagination" aria-label="Paginación">
        <button type="button" className="gym-exercises__page-button" disabled={!offset} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}><TbChevronLeft aria-hidden="true" focusable="false" />Anterior</button>
        <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, payload.total)} de {payload.total}</span>
        <button type="button" className="gym-exercises__page-button" disabled={offset + PAGE_SIZE >= payload.total} onClick={() => setOffset(offset + PAGE_SIZE)}>Siguiente<TbChevronRight aria-hidden="true" focusable="false" /></button>
      </nav>}
      {toast && <div className="gym-exercises__toast" role="status"><TbCircleCheck aria-hidden="true" focusable="false" /><span>{toast}</span>
        {createdExercise && <button type="button" className="gym-exercises__toast-action" onClick={() => { setSelected(createdExercise); setCreatedExercise(null); setToast(''); }}>Agregar imagen</button>}<button type="button" aria-label="Cerrar aviso" onClick={() => setToast('')}><TbX aria-hidden="true" focusable="false" /></button></div>}
      {pendingRemoval && (
        <ConfirmationSheet
          title={pendingRemoval.hasOriginalMedia
            ? '¿Volver a la imagen original de este ejercicio?'
            : '¿Quitar la imagen propia de este ejercicio?'}
          confirmLabel="Confirmar"
          pendingLabel="Actualizando…"
          busy={busyId === pendingRemoval.id}
          onConfirm={removeMedia}
          onClose={() => setPendingRemoval(null)}
        />
      )}
      {formTarget && (
        <ExerciseFormSheet api={api} exercise={formTarget.exercise} onSaved={handleExerciseSaved} onClose={() => setFormTarget(null)} />
      )}
      {renameTarget && (
        <ExerciseRenameSheet api={api} exercise={renameTarget} onSaved={handleRenamed} onClose={() => setRenameTarget(null)} />
      )}
      {archiveTarget && (
        <ConfirmationSheet
          title="¿Archivar este ejercicio?"
          description="Deja de aparecer en el buscador. Las rutinas que ya lo usan lo siguen mostrando."
          confirmLabel="Sí, archivar"
          pendingLabel="Archivando…"
          busy={busyId === archiveTarget.id}
          onConfirm={archiveExercise}
          onClose={() => setArchiveTarget(null)}
        />
      )}
      {selected && <ImageWizard
        exercise={selected}
        api={api}
        mediaDevices={mediaDevices}
        resizeImage={resizeImage}
        onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); setCreatedExercise(null); setToast('Imagen actualizada'); load(); }}
      />}
    </section>
  );
}

function ImageWizard({ exercise, api, mediaDevices, resizeImage, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [images, setImages] = useState([null, null]);
  const [cameraError, setCameraError] = useState('');
  const [stream, setStream] = useState(null);
  const [showGuide, setShowGuide] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const videoRef = useRef(null);
  const previousFocusRef = useRef(null);
  const streamRef = useRef(null);
  const stepsImage = images[step];

  const stopStream = useCallback(() => {
    const activeStream = streamRef.current;
    if (activeStream) activeStream.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const first = dialogRef.current && dialogRef.current.querySelector('button, input');
    if (first) first.focus();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (previousFocusRef.current && previousFocusRef.current.focus) previousFocusRef.current.focus();
    };
  }, []); // stream cleanup intentionally reads the mount snapshot; updated by stream effect below

  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream]);

  function close() { stopStream(); onClose(); }

  function onDialogKeyDown(event) {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    const controls = [...dialogRef.current.querySelectorAll('button:not([disabled]), input:not([disabled])')];
    if (!controls.length) return;
    const first = controls[0]; const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  async function openCamera() {
    setCameraError(''); stopStream();
    try {
      if (!mediaDevices || !mediaDevices.getUserMedia) throw new Error('unavailable');
      const activeStream = await mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = activeStream;
      setStream(activeStream);
    } catch (_error) { setCameraError('No se pudo abrir la cámara. Podés elegir un archivo.'); }
  }

  async function acceptPhoto(file) {
    if (!file) return;
    setError('');
    stopStream();
    try {
      const resized = await resizeImage(file);
      const dataUrl = await readDataUrl(resized);
      setImages((current) => current.map((image, index) => index === step ? { dataUrl, base64: dataUrl.split(',')[1] } : image));
    } catch (e) { setError(e.message || 'No se pudo procesar la imagen.'); }
  }

  function capture() {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) { setCameraError('La cámara todavía se está iniciando.'); return; }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    stopStream();
    canvas.toBlob((blob) => { if (blob) acceptPhoto(blob); else setError('No se pudo capturar la foto.'); }, 'image/jpeg', 0.92);
  }

  async function save() {
    setSaving(true); setError('');
    try {
      await api.saveExerciseMedia(exercise.id, { startImage: images[0].base64, endImage: images[1].base64 });
      onSaved();
    } catch (e) { setError(e.message || 'No se pudo guardar la imagen.'); }
    finally { setSaving(false); }
  }

  return (
    <div className="gym-image-wizard__backdrop">
      <section className="gym-image-wizard" role="dialog" aria-modal="true" aria-labelledby="gym-image-wizard-title" ref={dialogRef} onKeyDown={onDialogKeyDown}>
        <span className="gym-image-wizard__handle" aria-hidden="true" />
        <header className="gym-image-wizard__header">
          <div><p>Paso {step + 1} de 3</p><h2 id="gym-image-wizard-title">{STEPS[step]}</h2><span>{exercise.name}</span></div>
          <button type="button" className="gym-image-wizard__close" aria-label="Cerrar" onClick={close}><TbX aria-hidden="true" /></button>
        </header>
        {step < 2 ? <div className="gym-image-wizard__content">
          {stepsImage ? <div className="gym-image-wizard__photo"><img src={stepsImage.dataUrl} alt={`${STEPS[step]} capturada`} /><p>{STEPS[step]} lista</p><button type="button" className="gym-image-wizard__button gym-image-wizard__button--secondary" onClick={() => setImages((current) => current.map((image, index) => index === step ? null : image))}><TbRefresh aria-hidden="true" />Volver a sacar</button></div> : <>
            <div className="gym-image-wizard__camera">
              {stream ? <><video ref={videoRef} autoPlay playsInline muted aria-label="Vista de la cámara" />{step === 1 && showGuide && images[0] && <img className="gym-image-wizard__guide" src={images[0].dataUrl} alt="Guía de encuadre de la posición inicial" />}<button type="button" className="gym-image-wizard__capture" onClick={capture}><TbCamera aria-hidden="true" />Capturar</button></> : <div className="gym-image-wizard__camera-placeholder"><TbCamera aria-hidden="true" /><span>Usá la cámara o elegí una foto</span></div>}
            </div>
            {step === 1 && images[0] && <label className="gym-image-wizard__guide-toggle"><input type="checkbox" checked={showGuide} onChange={(event) => setShowGuide(event.target.checked)} /> Mostrar guía</label>}
            {cameraError && <p className="gym-image-wizard__camera-error" role="alert">{cameraError}</p>}
            <div className="gym-image-wizard__actions"><button type="button" className="gym-image-wizard__button gym-image-wizard__button--primary" onClick={openCamera}><TbCamera aria-hidden="true" /> Sacar foto</button>
              <label className="gym-image-wizard__button gym-image-wizard__button--secondary gym-image-wizard__file"><TbPhoto aria-hidden="true" /> Elegir archivo<input aria-label="Elegir archivo" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => acceptPhoto(event.target.files && event.target.files[0])} /></label>
            </div>
          </>}
        </div> : <div className="gym-image-wizard__preview"><p>Así va a quedar</p><div className="gym-image-wizard__crossfade"><img src={images[0].dataUrl} alt="Posición inicial" /><img src={images[1].dataUrl} alt="Posición final" /></div></div>}
        {error && <p className="gym-image-wizard__error" role="alert">{error}</p>}
        <footer className="gym-image-wizard__footer">
          {step > 0 && <button type="button" className="gym-image-wizard__button gym-image-wizard__button--secondary" onClick={() => { stopStream(); setError(''); setStep(step - 1); }}>Atrás</button>}
          {step < 2 ? <button type="button" className="gym-image-wizard__button gym-image-wizard__button--primary" disabled={!images[step]} onClick={() => { stopStream(); setStep(step + 1); }}>Continuar</button> : <button type="button" className="gym-image-wizard__button gym-image-wizard__button--primary" disabled={saving} onClick={save}>{saving && <span className="gym-image-wizard__spinner" aria-hidden="true" />}{saving ? 'Generando imagen…' : 'Guardar'}</button>}
        </footer>
      </section>
    </div>
  );
}
